import type { CoreConfiguration, GooglePayConfiguration, ApplePayConfiguration, UIElement } from '@adyen/adyen-web';
import {
  ADYEN_CARD_BRANDS,
  ADYEN_PAYMENT_METHOD_TYPE,
  ADYEN_PAYMENT_METHOD_NAME,
  ADYEN_CARD_FIELD,
  ADYEN_DATE_FIELD,
  ADYEN_CVC_FIELD,
  ADYEN_GOOGLE_PAY_CONFIG,
  ADYEN_GOOGLE_PAY_TYPE,
  ADYEN_APPLE_PAY_TYPE,
} from '@/constants/adyen';
import type { Customer, SavedCard, PaymentMethod as CheckoutPaymentMethod } from '@/types/checkout';
import { ProviderEnum } from '@/types/common';
import { getPaymentRequestAmount } from './pricing';
import type {
  AdyenConfig,
  AdyenGooglePayConfig,
  AdyenApplePayConfig,
  AdyenApplePayPaymentMethod,
  CardDetails,
  CardFieldEvent,
  AdyenCardComponent,
  CreateGooglePayComponentParams,
  CreateApplePayComponentParams,
} from '@/types/adyen';
import { getBrowserInfo } from './browser';
import { getLocale } from './get-locale-util';

export const mountAdyenComponent = (element: UIElement, container: HTMLElement | null) => {
  if (container) {
    element.mount(container);
  }
};

export const createAdyenConfiguration = (config: AdyenConfig, customer?: Customer): CoreConfiguration => {
  const { pspLocale } = getLocale();
  return {
    paymentMethodsResponse: {
      paymentMethods: [
        {
          brands: [...ADYEN_CARD_BRANDS],
          name: ADYEN_PAYMENT_METHOD_NAME,
          type: ADYEN_PAYMENT_METHOD_TYPE,
        },
      ],
    },
    environment: config.env || 'test',
    countryCode: customer?.geoLocator?.countryCode2 || customer?.taxCountry,
    clientKey: config.clientKey,
    showPayButton: false,
    locale: pspLocale,
  };
};

export const isValidCardDetails = (details: CardDetails): boolean =>
  !!(details.bin && details.endDigits && details.expiryDate);

export const createCardDetails = (): CardDetails => ({
  bin: '',
  endDigits: '',
  expiryDate: '',
  cardType: '',
});

export const getAutoAdvanceAdyenFieldFocus = (
  event: Pick<CardFieldEvent, 'valid' | 'fieldType'>,
  cardComponent?: Pick<AdyenCardComponent, 'setFocusOn'>
) => {
  if (!event.valid || !cardComponent?.setFocusOn) return;

  const nextField =
    event.fieldType === ADYEN_CARD_FIELD
      ? ADYEN_DATE_FIELD
      : event.fieldType === ADYEN_DATE_FIELD
        ? ADYEN_CVC_FIELD
        : null;

  if (!nextField) return;

  setTimeout(() => cardComponent.setFocusOn?.(nextField), 0);
};

export const getAdyenConfig = (paymentMethods: unknown[], selectedMethod: string): AdyenConfig | undefined => {
  const method = (paymentMethods as { paymentMethodName: string; publicConfig?: AdyenConfig }[]).find(
    (pm) => pm.paymentMethodName === selectedMethod
  );
  return method?.publicConfig;
};

export interface AdyenCardPaymentParams {
  email: string;
  cardholderName: string;
  shouldSaveCard: boolean;
  paymentMethod: Record<string, unknown>;
  browserInfo: Record<string, unknown>;
  cardDetails: CardDetails;
  paymentMethodId: string;
  paymentMethodDisplayName: string;
  forterToken?: string;
}

export const buildAdyenSavedCreditCardPaymentRequest = (
  selectedCard: SavedCard,
  customer: Customer,
  adyenSettings: CheckoutPaymentMethod,
  cvc: string
) => {
  return {
    paymentProvider: ProviderEnum.ADYEN,
    receipt_email: customer.email,
    paymentMethodDisplayName: adyenSettings.paymentMethodName,
    paymentMethodId: adyenSettings.id,
    params: {
      paymentToken: selectedCard.pspCardIdentifier,
      cvc,
      brand: selectedCard.brand,
      browserInfo: getBrowserInfo(),
    },
  };
};

export const getAdyenCardData = (
  cardDetails: CardDetails,
  paymentMethodData?: Record<string, unknown>
): {
  tokenResponse?: {
    paymentMethodData: Record<string, unknown>;
    browserInfo: Record<string, unknown>;
  };
  cardDetails: {
    bin: string;
    last4digits: string;
    cardType: string;
    ccExpMonth: string;
    ccExpYear: string;
    brand: string;
  };
} | null => {
  if (!isValidCardDetails(cardDetails)) {
    return null;
  }

  const [expMonth, expYear] = cardDetails.expiryDate.split('/');

  return {
    tokenResponse: paymentMethodData
      ? {
          paymentMethodData,
          browserInfo: cardDetails.browserInfo || {},
        }
      : undefined,
    cardDetails: {
      bin: cardDetails.bin,
      last4digits: cardDetails.endDigits,
      cardType: cardDetails.cardType || 'scheme',
      ccExpMonth: expMonth || '',
      ccExpYear: expYear || '',
      brand: cardDetails.cardType || 'scheme',
    },
  };
};

export const createAdyenCardPayment = (params: AdyenCardPaymentParams) => ({
  paymentProvider: 'adyen',
  receipt_email: params.email,
  paymentMethodDisplayName: params.paymentMethodDisplayName,
  paymentMethodId: params.paymentMethodId,
  params: {
    browserInfo: params.browserInfo,
    paymentMethod: {
      ...(params.cardDetails.paymentMethodData || {}),
      holderName: params.cardholderName,
    },
    cardHolderName: params.cardholderName,
    shouldSaveCard: params.shouldSaveCard,
    cardDetails: {
      bin: params.cardDetails.bin,
      lastFourDigits: params.cardDetails.endDigits,
      nameOnCard: params.cardholderName,
      expirationMonth: params.cardDetails.expiryDate.split('/')[0],
      expirationYear: params.cardDetails.expiryDate.split('/')[1],
      cardType: params.cardDetails.cardType || 'scheme',
    },
  },
  ...(params.forterToken && {
    fraudDetection: {
      forterTokenCookie: params.forterToken,
    },
  }),
});

export const buildAdyenGenericPaymentRequest = (
  params: Record<string, string>,
  adyenSettings: CheckoutPaymentMethod,
  customer: Customer
) => {
  return {
    params: {
      additionalInputFields: { ...params },
    },
    paymentMethodDisplayName: adyenSettings.paymentMethodDisplayName,
    paymentMethodId: adyenSettings.id,
    paymentProvider: ProviderEnum.ADYEN,
    receipt_email: params.email ?? params.shopperEmail ?? customer.email ?? '',
  };
};

export const createAdyenGooglePayConfiguration = (
  config: AdyenGooglePayConfig,
  customer?: Customer,
  paymentMethodConfig?: Record<string, unknown>
): CoreConfiguration => {
  const { pspLocale } = getLocale();
  const paymentMethods = [
    paymentMethodConfig && typeof paymentMethodConfig === 'object' && 'type' in paymentMethodConfig
      ? (paymentMethodConfig as { type: string; name: string })
      : { type: ADYEN_GOOGLE_PAY_TYPE, name: 'Google Pay' },
  ];

  const environment = (config.environment || config.env || 'test') as 'test' | 'live';

  return {
    paymentMethodsResponse: {
      paymentMethods,
    },
    environment,
    countryCode: customer?.geoLocator?.countryCode2 || customer?.taxCountry,
    clientKey: config.clientKey,
    locale: pspLocale,
  };
};

export const buildAdyenGooglePayPaymentRequest = (
  params: { paymentMethod: Record<string, unknown>; browserInfo?: Record<string, unknown>; email?: string },
  adyenSettings: CheckoutPaymentMethod,
  customer: Customer
) => ({
  paymentProvider: ProviderEnum.ADYEN,
  receipt_email: params.email ?? customer.email ?? '',
  paymentMethodDisplayName: adyenSettings.paymentMethodDisplayName,
  paymentMethodId: adyenSettings.id,
  params: {
    browserInfo: params.browserInfo ?? {},
    paymentMethod: params.paymentMethod,
  },
});

export const getGooglePayComponentOptions = (params: CreateGooglePayComponentParams): GooglePayConfiguration => {
  const { googlePayPaymentMethod, customer, pricing, handlers } = params;

  const gatewayMerchantId = String(
    (googlePayPaymentMethod?.publicConfig as AdyenGooglePayConfig | undefined)?.merchantId ?? ''
  );
  const googlePayConfiguration = googlePayPaymentMethod?.paymentMethodConfig?.configuration ?? {};

  return {
    ...ADYEN_GOOGLE_PAY_CONFIG,
    configuration: {
      gatewayMerchantId,
      ...googlePayConfiguration,
    },
    transactionInfo: {
      displayItems: [],
      countryCode: customer?.geoLocator?.countryCode2 ?? customer?.taxCountry ?? '',
      currencyCode: pricing.currencyCode,
      totalPriceStatus: 'FINAL',
      totalPrice: getPaymentRequestAmount(pricing.priceBreakdown.priceTotal, pricing),
      totalPriceLabel: 'Total',
    },
    ...(handlers as GooglePayConfiguration),
  };
};

export const getTopLevelDomain = (): string => {
  try {
    const isInIframe = window.location !== window.parent.location;
    // Safari private/incognito mode returns empty document.referrer in iframes
    const topLevelUrl = isInIframe ? document.referrer || document.location.href : document.location.href;
    if (!topLevelUrl) {
      return window.location.hostname;
    }
    return new URL(topLevelUrl).hostname;
  } catch {
    return window.location.hostname;
  }
};

export const getAdyenApplePayConfig = (): ApplePayConfiguration => {
  return {
    isExpress: true,
    showPayButton: true,
    requiredBillingContactFields: ['email'],
    requiredShippingContactFields: ['email'],
    domainName: getTopLevelDomain(),
  };
};

export const createAdyenApplePayConfiguration = (
  applePayPaymentMethod: AdyenApplePayPaymentMethod,
  customer?: Customer
): CoreConfiguration => {
  const config = applePayPaymentMethod.publicConfig as AdyenApplePayConfig;
  const paymentMethodConfig = applePayPaymentMethod.paymentMethodConfig;
  const { pspLocale } = getLocale();
  const paymentMethods = [
    paymentMethodConfig && typeof paymentMethodConfig === 'object' && 'type' in paymentMethodConfig
      ? (paymentMethodConfig as { type: string; name: string })
      : { type: ADYEN_APPLE_PAY_TYPE, name: 'Apple Pay' },
  ];

  const environment = (config.environment || config.env || 'test') as 'test' | 'live';

  return {
    paymentMethodsResponse: {
      paymentMethods,
    },
    environment,
    countryCode: customer?.geoLocator?.countryCode2 || customer?.taxCountry,
    clientKey: config.clientKey,
    locale: pspLocale,
  };
};

export const buildAdyenApplePayPaymentRequest = (
  params: {
    paymentMethod: Record<string, unknown>;
    paymentMethodDetails: Record<string, unknown>;
    email?: string;
  },
  adyenSettings: CheckoutPaymentMethod,
  customer: Customer
) => ({
  paymentProvider: ProviderEnum.ADYEN,
  receipt_email: params.email ?? customer.email ?? '',
  paymentMethodDisplayName: adyenSettings.paymentMethodDisplayName,
  paymentMethodId: adyenSettings.id,
  params: {
    paymentMethod: {
      ...params.paymentMethod,
      type: 'applepay',
    },
    paymentMethodDetails: params.paymentMethodDetails,
  },
});

export const getApplePayComponentOptions = (params: CreateApplePayComponentParams): ApplePayConfiguration => {
  const { applePayPaymentMethod, pricing, handlers } = params;

  const applePayConfiguration = applePayPaymentMethod?.paymentMethodConfig?.configuration ?? {};

  return {
    ...getAdyenApplePayConfig(),
    amount: {
      value: Math.round(pricing.priceBreakdown.priceTotal),
      currency: pricing.currencyCode,
    },
    configuration: applePayConfiguration,
    ...handlers,
  };
};
